import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * College Notebook Hook
 *
 * Fetches and transforms college strategy data for display in the College Notebook sidebar.
 * Pulls data from college_journey_colleges and optionally essay slot data.
 *
 * @param {string} collegeId - The college_journey_colleges ID
 * @param {string} essaySlotId - Optional essay slot ID for essay-specific context
 * @returns {Object} { notebookData, loading, error, refresh }
 */
export const useCollegeNotebook = (collegeId, essaySlotId = null) => {
  const [notebookData, setNotebookData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collegeNotes, setCollegeNotes] = useState([]);
  const [essayNotes, setEssayNotes] = useState([]);
  const [notesSaving, setNotesSaving] = useState(false);
  const [highlights, setHighlights] = useState([]);
  const [highlightsLoading, setHighlightsLoading] = useState(false);

  // Fetch highlights
  const fetchHighlights = useCallback(async () => {
    if (!collegeId) return;

    try {
      setHighlightsLoading(true);

      const { data, error: highlightsError } = await supabase
        .from('college_notebook_highlights')
        .select('*')
        .eq('college_id', collegeId)
        .order('created_at', { ascending: false });

      if (highlightsError) throw highlightsError;

      setHighlights(data || []);
    } catch (err) {
      console.error('Error fetching highlights:', err);
    } finally {
      setHighlightsLoading(false);
    }
  }, [collegeId]);

  const fetchNotebookData = useCallback(async () => {
    if (!collegeId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch college data (including notebook_notes)
      const { data: college, error: collegeError } = await supabase
        .from('college_journey_colleges')
        .select('*')
        .eq('id', collegeId)
        .single();

      if (collegeError) throw collegeError;

      // Set college notes - handle array, string, and stringified JSON formats
      const notes = college.notebook_notes;
      if (Array.isArray(notes)) {
        setCollegeNotes(notes);
      } else if (typeof notes === 'string' && notes.trim()) {
        // Check if it's stringified JSON array
        if (notes.trim().startsWith('[{')) {
          try {
            const parsed = JSON.parse(notes);
            if (Array.isArray(parsed)) {
              setCollegeNotes(parsed);
            } else {
              // Not an array, convert to legacy note
              setCollegeNotes([{
                id: 'legacy-' + Date.now(),
                content: notes,
                created_at: new Date().toISOString(),
                type: 'legacy'
              }]);
            }
          } catch (e) {
            console.warn('Failed to parse notebook_notes JSON:', e);
            // Parse failed, treat as legacy string
            setCollegeNotes([{
              id: 'legacy-' + Date.now(),
              content: notes,
              created_at: new Date().toISOString(),
              type: 'legacy'
            }]);
          }
        } else {
          // Plain string legacy format
          setCollegeNotes([{
            id: 'legacy-' + Date.now(),
            content: notes,
            created_at: new Date().toISOString(),
            type: 'legacy'
          }]);
        }
      } else {
        setCollegeNotes([]);
      }

      // Optionally fetch essay slot data (including notebook_notes)
      let essaySlot = null;
      if (essaySlotId) {
        const { data: slotData, error: slotError } = await supabase
          .from('college_journey_essay_slots')
          .select('*')
          .eq('id', essaySlotId)
          .single();

        if (!slotError && slotData) {
          essaySlot = slotData;
          // Handle essay notes - array, string, and stringified JSON formats
          const essayNotesData = slotData.notebook_notes;
          if (Array.isArray(essayNotesData)) {
            setEssayNotes(essayNotesData);
          } else if (typeof essayNotesData === 'string' && essayNotesData.trim()) {
            // Check if it's stringified JSON array
            if (essayNotesData.trim().startsWith('[{')) {
              try {
                const parsed = JSON.parse(essayNotesData);
                if (Array.isArray(parsed)) {
                  setEssayNotes(parsed);
                } else {
                  // Not an array, convert to legacy note
                  setEssayNotes([{
                    id: 'legacy-essay-' + Date.now(),
                    content: essayNotesData,
                    created_at: new Date().toISOString(),
                    type: 'legacy'
                  }]);
                }
              } catch (e) {
                console.warn('Failed to parse essay notebook_notes JSON:', e);
                // Parse failed, treat as legacy string
                setEssayNotes([{
                  id: 'legacy-essay-' + Date.now(),
                  content: essayNotesData,
                  created_at: new Date().toISOString(),
                  type: 'legacy'
                }]);
              }
            } else {
              // Plain string legacy format
              setEssayNotes([{
                id: 'legacy-essay-' + Date.now(),
                content: essayNotesData,
                created_at: new Date().toISOString(),
                type: 'legacy'
              }]);
            }
          } else {
            setEssayNotes([]);
          }
        }
      } else {
        setEssayNotes([]);
      }

      // Transform data into notebook structure
      const transformed = transformCollegeData(college, essaySlot);
      setNotebookData(transformed);

      // Fetch highlights for this college
      await fetchHighlights();

    } catch (err) {
      console.error('Error fetching notebook data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [collegeId, essaySlotId, fetchHighlights]);

  // Create highlight
  const createHighlight = useCallback(async (highlightData) => {
    if (!collegeId) return;

    try {
      const { data, error: createError } = await supabase
        .from('college_notebook_highlights')
        .insert([{
          college_id: collegeId,
          essay_slot_id: essaySlotId,
          highlighted_text: highlightData.text,
          section_name: highlightData.section || 'general',
          category: highlightData.category || 'general',
          context: highlightData.context || null
        }])
        .select()
        .single();

      if (createError) throw createError;

      // Add to local state
      setHighlights(prev => [data, ...prev]);
      return data;
    } catch (err) {
      console.error('Error creating highlight:', err);
      throw err;
    }
  }, [collegeId, essaySlotId]);

  // Delete highlight
  const deleteHighlight = useCallback(async (highlightId) => {
    try {
      const { error: deleteError } = await supabase
        .from('college_notebook_highlights')
        .delete()
        .eq('id', highlightId);

      if (deleteError) throw deleteError;

      // Remove from local state
      setHighlights(prev => prev.filter(h => h.id !== highlightId));
    } catch (err) {
      console.error('Error deleting highlight:', err);
      throw err;
    }
  }, []);

  // Update highlight category
  const updateHighlightCategory = useCallback(async (highlightId, newCategory) => {
    try {
      const { error: updateError } = await supabase
        .from('college_notebook_highlights')
        .update({ category: newCategory })
        .eq('id', highlightId);

      if (updateError) throw updateError;

      // Update local state
      setHighlights(prev =>
        prev.map(h => h.id === highlightId ? { ...h, category: newCategory } : h)
      );
    } catch (err) {
      console.error('Error updating highlight:', err);
      throw err;
    }
  }, []);

  // Save college notes (debounced at component level)
  const saveCollegeNotes = useCallback(async (notes) => {
    if (!collegeId) return;

    try {
      setNotesSaving(true);

      const { error: updateError } = await supabase
        .from('college_journey_colleges')
        .update({ notebook_notes: notes })
        .eq('id', collegeId);

      if (updateError) throw updateError;

      setCollegeNotes(notes);
    } catch (err) {
      console.error('Error saving college notes:', err);
      setError(err.message);
    } finally {
      setNotesSaving(false);
    }
  }, [collegeId]);

  // Save essay notes (debounced at component level)
  const saveEssayNotes = useCallback(async (notes) => {
    if (!essaySlotId) return;

    try {
      setNotesSaving(true);

      const { error: updateError } = await supabase
        .from('college_journey_essay_slots')
        .update({ notebook_notes: notes })
        .eq('id', essaySlotId);

      if (updateError) throw updateError;

      setEssayNotes(notes);
    } catch (err) {
      console.error('Error saving essay notes:', err);
      setError(err.message);
    } finally {
      setNotesSaving(false);
    }
  }, [essaySlotId]);

  useEffect(() => {
    fetchNotebookData();
  }, [fetchNotebookData]);

  return {
    notebookData,
    loading,
    error,
    refresh: fetchNotebookData,
    // Notes functionality
    collegeNotes,
    essayNotes,
    saveCollegeNotes,
    saveEssayNotes,
    notesSaving,
    // Highlights functionality
    highlights,
    highlightsLoading,
    createHighlight,
    deleteHighlight,
    updateHighlightCategory,
    refreshHighlights: fetchHighlights
  };
};

/**
 * Transform raw college data into structured notebook content
 */
const transformCollegeData = (college, essaySlot = null) => {
  if (!college) return null;

  return {
    college: college.name,
    major: college.major_of_interest || college.academic_focus,

    // Essay context (if applicable)
    essayPrompt: essaySlot ? {
      prompt: essaySlot.prompt_text,
      wordLimit: essaySlot.word_limit,
      essayType: essaySlot.essay_type,
      category: essaySlot.prompt_category
    } : null,

    // Intelligent Insights (from AI analysis)
    insights: parseInsights(college),

    // Specific Positioning (from strategic fit)
    positioning: parsePositioning(college),

    // Your Strategic Edge
    strategicEdge: parseStrategicEdge(college),

    // What This School Values
    schoolValues: parseSchoolValues(college),

    // Your Value Proposition
    valueProposition: parseValueProposition(college),

    // Competitive Position (Your Advantages)
    advantages: parseAdvantages(college),

    // Aspects to Address (Growth areas)
    aspectsToAddress: parseAspectsToAddress(college),

    // Academic Fit Analysis
    academicFit: parseAcademicFit(college),

    // Culture Fit Analysis
    cultureFit: parseCultureFit(college),

    // Your Edge and Fit (hidden advantages)
    edgeAndFit: parseEdgeAndFit(college),

    // Strategic Research Bank
    researchBank: parseResearchBank(college),

    // Metadata
    lastUpdated: college.updated_at,
    aiInsightsUpdated: college.ai_insights_updated_at
  };
};

/**
 * Parse Intelligent Insights
 * These are high-level strategic insights about the college
 */
const parseInsights = (college) => {
  const insights = [];

  // From ai_insights field (Duke format: {valuesMatch, academicFit, locationAdvantages})
  if (college.ai_insights) {
    try {
      const parsed = typeof college.ai_insights === 'string'
        ? JSON.parse(college.ai_insights)
        : college.ai_insights;

      // Handle Duke format with specific keys
      if (parsed.valuesMatch) {
        insights.push({
          icon: 'heart',
          color: 'teal',
          title: 'VALUES MATCH',
          content: parsed.valuesMatch
        });
      }
      if (parsed.academicFit) {
        insights.push({
          icon: 'graduation',
          color: 'indigo',
          title: 'ACADEMIC FIT',
          content: parsed.academicFit
        });
      }
      if (parsed.locationAdvantages) {
        insights.push({
          icon: 'location',
          color: 'azure',
          title: 'LOCATION',
          content: parsed.locationAdvantages
        });
      }

      // Handle array format
      if (Array.isArray(parsed)) {
        insights.push(...parsed);
      } else if (parsed.insights && Array.isArray(parsed.insights)) {
        insights.push(...parsed.insights);
      }
    } catch (e) {
      // If it's plain text, wrap it
      if (typeof college.ai_insights === 'string') {
        insights.push({
          icon: 'lightbulb',
          color: 'teal',
          title: 'AI INSIGHT',
          content: college.ai_insights
        });
      }
    }
  }

  // From strategic_fit_analysis
  if (college.strategic_fit_analysis && Object.keys(college.strategic_fit_analysis).length > 0) {
    const fit = college.strategic_fit_analysis;
    if (fit.overall_fit) {
      insights.push({
        icon: 'target',
        color: 'indigo',
        title: 'STRATEGIC FIT',
        content: fit.overall_fit
      });
    }
    if (fit.values_alignment) {
      insights.push({
        icon: 'heart',
        color: 'coral',
        title: 'VALUES ALIGNMENT',
        content: fit.values_alignment
      });
    }
  }

  return insights;
};

/**
 * Parse Specific Positioning
 * Concrete actions to position yourself effectively
 */
const parsePositioning = (college) => {
  const positioning = [];

  if (college.application_strategy) {
    positioning.push(college.application_strategy);
  }

  if (college.recommended_strategy) {
    positioning.push(college.recommended_strategy);
  }

  // From strategic_fit_analysis
  if (college.strategic_fit_analysis && Object.keys(college.strategic_fit_analysis).length > 0) {
    const fit = college.strategic_fit_analysis;
    if (fit.positioning_strategy) {
      const strategies = fit.positioning_strategy;
      if (Array.isArray(strategies)) {
        positioning.push(...strategies);
      } else if (typeof strategies === 'string') {
        positioning.push(strategies);
      }
    }
  }

  // From improvement_plan (Duke format has reachPositioning)
  if (positioning.length === 0 && college.improvement_plan) {
    try {
      const plan = typeof college.improvement_plan === 'string'
        ? JSON.parse(college.improvement_plan)
        : college.improvement_plan;

      if (plan.reachPositioning && Array.isArray(plan.reachPositioning)) {
        plan.reachPositioning.forEach(item => {
          if (item.action) {
            positioning.push(item.action);
          }
        });
      }

      // Also add immediate priorities if they have positioning advice
      if (plan.immediatePriorities && Array.isArray(plan.immediatePriorities)) {
        plan.immediatePriorities.forEach(item => {
          if (item.action) {
            positioning.push(item.action);
          }
        });
      }
    } catch (e) {
      console.error('Error parsing improvement_plan for positioning:', e);
    }
  }

  return positioning.filter(p => p && p.trim().length > 0);
};

/**
 * Parse Strategic Edge
 * What makes you stand out at this school
 */
const parseStrategicEdge = (college) => {
  const edge = [];

  // From competitive_position
  if (college.competitive_position?.strategic_edge) {
    const edgeData = college.competitive_position.strategic_edge;
    if (Array.isArray(edgeData)) {
      edge.push(...edgeData);
    }
  }

  // From strategic_fit_analysis
  if (college.strategic_fit_analysis && Object.keys(college.strategic_fit_analysis).length > 0) {
    const fit = college.strategic_fit_analysis;
    if (fit.your_edge) {
      const edgeData = fit.your_edge;
      if (Array.isArray(edgeData)) {
        edge.push(...edgeData);
      } else if (typeof edgeData === 'string') {
        edge.push(edgeData);
      }
    }
  }

  // From student_advantages (these are your strategic edges)
  if (edge.length === 0 && college.student_advantages) {
    try {
      const advantages = typeof college.student_advantages === 'string'
        ? JSON.parse(college.student_advantages)
        : college.student_advantages;

      if (Array.isArray(advantages) && advantages.length > 0) {
        // Use student advantages as strategic edge
        edge.push(...advantages);
      }
    } catch (e) {
      console.error('Error parsing student_advantages for strategic edge:', e);
    }
  }

  return edge;
};

/**
 * Parse School Values
 * What the school prioritizes in applicants
 */
const parseSchoolValues = (college) => {
  const values = [];

  // From strategic_fit_analysis
  if (college.strategic_fit_analysis && Object.keys(college.strategic_fit_analysis).length > 0) {
    const fit = college.strategic_fit_analysis;
    if (fit.school_values && Array.isArray(fit.school_values)) {
      values.push(...fit.school_values);
    }
  }

  // From culture_fit_analysis
  if (college.culture_fit_analysis) {
    try {
      const parsed = typeof college.culture_fit_analysis === 'string'
        ? JSON.parse(college.culture_fit_analysis)
        : college.culture_fit_analysis;

      // Extract values from various fields that indicate what the school prioritizes
      if (parsed.core_values && Array.isArray(parsed.core_values)) {
        values.push(...parsed.core_values);
      }
    } catch (e) {
      console.error('Error parsing culture_fit_analysis for school values:', e);
    }
  }

  // If still no values, extract from ai_insights valuesMatch
  if (values.length === 0 && college.ai_insights) {
    try {
      const insights = typeof college.ai_insights === 'string'
        ? JSON.parse(college.ai_insights)
        : college.ai_insights;

      if (insights.valuesMatch) {
        // Extract key phrases about what the school values
        values.push(insights.valuesMatch);
      }
    } catch (e) {
      console.error('Error parsing ai_insights for school values:', e);
    }
  }

  return values;
};

/**
 * Parse Value Proposition
 * Your unique pitch to this school
 */
const parseValueProposition = (college) => {
  // First check the direct field
  if (college.value_proposition) {
    if (typeof college.value_proposition === 'string') {
      return { text: college.value_proposition };
    }
    return college.value_proposition;
  }

  // Check in ai_suggestions (Duke format)
  if (college.ai_suggestions) {
    try {
      const suggestions = typeof college.ai_suggestions === 'string'
        ? JSON.parse(college.ai_suggestions)
        : college.ai_suggestions;

      if (suggestions.value_proposition) {
        return { text: suggestions.value_proposition };
      }
    } catch (e) {
      console.error('Error parsing ai_suggestions for value_proposition:', e);
    }
  }

  return null;
};

/**
 * Parse Your Advantages
 * Specific strengths for this school
 */
const parseAdvantages = (college) => {
  const advantages = [];

  // From student_advantages (primary source)
  if (college.student_advantages && Array.isArray(college.student_advantages)) {
    advantages.push(...college.student_advantages);
  }

  // Fallback to your_advantages
  if (advantages.length === 0 && college.your_advantages && Array.isArray(college.your_advantages)) {
    advantages.push(...college.your_advantages);
  }

  // From competitive_position
  if (advantages.length === 0 && college.competitive_position?.advantages) {
    const compAdvantages = college.competitive_position.advantages;
    if (Array.isArray(compAdvantages)) {
      advantages.push(...compAdvantages);
    }
  }

  return advantages;
};

/**
 * Parse Aspects to Address
 * Areas for growth or improvement
 */
const parseAspectsToAddress = (college) => {
  const aspects = [];

  // From aspects_to_address
  if (college.aspects_to_address && Array.isArray(college.aspects_to_address)) {
    aspects.push(...college.aspects_to_address);
  }

  // From competitive_position
  if (aspects.length === 0 && college.competitive_position?.weaknesses) {
    const weaknesses = college.competitive_position.weaknesses;
    if (Array.isArray(weaknesses)) {
      aspects.push(...weaknesses);
    }
  }

  return aspects;
};

/**
 * Parse Academic Fit Analysis
 */
const parseAcademicFit = (college) => {
  if (!college.academic_fit_analysis) return null;

  try {
    const parsed = typeof college.academic_fit_analysis === 'string'
      ? JSON.parse(college.academic_fit_analysis)
      : college.academic_fit_analysis;

    // Handle empty object
    if (Object.keys(parsed).length === 0) return null;

    return {
      summary: parsed.summary || parsed.label,
      strengths: parsed.strengths || parsed.majorProgramFit || parsed.distinctiveOpportunities || [],
      considerations: parsed.considerations || parsed.challenges || [],
      programs: parsed.relevant_programs || [],
      academicCultureMatch: parsed.academic_culture_match || []
    };
  } catch (e) {
    console.error('Error parsing academic_fit_analysis:', e);
    return null;
  }
};

/**
 * Parse Culture Fit Analysis
 */
const parseCultureFit = (college) => {
  if (!college.culture_fit_analysis) return null;

  try {
    const parsed = typeof college.culture_fit_analysis === 'string'
      ? JSON.parse(college.culture_fit_analysis)
      : college.culture_fit_analysis;

    // Handle empty object
    if (Object.keys(parsed).length === 0) return null;

    return {
      summary: parsed.summary || parsed.label,
      alignment: parsed.alignment || parsed.campus_vibe_community || parsed.lifestyleEnvironment || [],
      potential_challenges: parsed.potential_challenges || parsed.considerations || [],
      campus_culture: parsed.campus_culture,
      socialFit: parsed.social_extracurricular_fit || []
    };
  } catch (e) {
    console.error('Error parsing culture_fit_analysis:', e);
    return null;
  }
};

/**
 * Parse Edge and Fit (Hidden advantages, why this fits better than you think)
 */
const parseEdgeAndFit = (college) => {
  const edgeAndFit = {
    yourEdge: [],
    mayNotHaveConsidered: [],
    whyThisFits: [],
    hiddenAdvantage: []
  };

  // Check strategic_fit_analysis
  if (college.strategic_fit_analysis && Object.keys(college.strategic_fit_analysis).length > 0) {
    const fit = college.strategic_fit_analysis;

    if (fit.your_edge) {
      edgeAndFit.yourEdge = Array.isArray(fit.your_edge) ? fit.your_edge : [fit.your_edge];
    }

    if (fit.hidden_advantages) {
      edgeAndFit.hiddenAdvantage = Array.isArray(fit.hidden_advantages)
        ? fit.hidden_advantages
        : [fit.hidden_advantages];
    }

    if (fit.why_this_fits) {
      edgeAndFit.whyThisFits = Array.isArray(fit.why_this_fits)
        ? fit.why_this_fits
        : [fit.why_this_fits];
    }

    if (fit.unexpected_alignments) {
      edgeAndFit.mayNotHaveConsidered = Array.isArray(fit.unexpected_alignments)
        ? fit.unexpected_alignments
        : [fit.unexpected_alignments];
    }
  }

  // Check competitive_position for Duke format
  if (college.competitive_position && college.competitive_position.tier_insights) {
    const tierInsights = college.competitive_position.tier_insights;

    if (tierInsights.hiddenAdvantage) {
      edgeAndFit.hiddenAdvantage.push(tierInsights.hiddenAdvantage);
    }

    if (tierInsights.betterThanYouThink) {
      edgeAndFit.whyThisFits.push(tierInsights.betterThanYouThink);
    }

    if (tierInsights.mayNotKnow && Array.isArray(tierInsights.mayNotKnow)) {
      edgeAndFit.mayNotHaveConsidered.push(...tierInsights.mayNotKnow);
    }
  }

  return edgeAndFit;
};

/**
 * Parse Strategic Research Bank
 * Specific programs, people, opportunities
 */
const parseResearchBank = (college) => {
  const research = {
    programs: [],
    culture: [],
    opportunities: []
  };

  // Check direct strategic_research field
  if (college.strategic_research) {
    try {
      const parsed = typeof college.strategic_research === 'string'
        ? JSON.parse(college.strategic_research)
        : college.strategic_research;

      if (parsed.programs) research.programs = parsed.programs;
      if (parsed.culture) research.culture = parsed.culture;
      if (parsed.opportunities) research.opportunities = parsed.opportunities;
    } catch (e) {
      // If plain text, store in general area
      if (typeof college.strategic_research === 'string') {
        research.programs.push({
          name: 'Research Notes',
          details: [college.strategic_research]
        });
      }
    }
  }

  // Check in ai_suggestions (Duke format)
  if (!college.strategic_research && college.ai_suggestions) {
    try {
      const suggestions = typeof college.ai_suggestions === 'string'
        ? JSON.parse(college.ai_suggestions)
        : college.ai_suggestions;

      if (suggestions.strategic_research) {
        // It's a long markdown text - extract sections
        const text = suggestions.strategic_research;

        // Try to extract program names and details
        const programMatches = text.match(/###\s+([^\n]+)\n([\s\S]*?)(?=###|\n##|\Z)/g);
        if (programMatches) {
          programMatches.forEach(match => {
            const lines = match.split('\n').filter(l => l.trim());
            const name = lines[0].replace(/###\s+/, '').trim();
            const details = lines.slice(1)
              .filter(l => l.includes('-') || l.includes('•') || l.includes(':'))
              .map(l => l.replace(/^[-•*]\s*/, '').trim());

            if (details.length > 0) {
              research.programs.push({ name, details: details.slice(0, 5) }); // Limit to 5 details per program
            }
          });
        }

        // Extract cultural insights
        const cultureMatch = text.match(/###\s+.*Culture.*\n([\s\S]*?)(?=###|\n##|\Z)/i);
        if (cultureMatch) {
          const cultureItems = cultureMatch[1]
            .split('\n')
            .filter(l => l.includes('-') || l.includes('•'))
            .map(l => l.replace(/^[-•*]\s*/, '').trim())
            .filter(l => l.length > 0);
          research.culture = cultureItems.slice(0, 8); // Limit to 8 items
        }
      }
    } catch (e) {
      console.error('Error parsing ai_suggestions for strategic_research:', e);
    }
  }

  // Add honors program info if available
  if (college.honors_program_info?.hasProgram) {
    const honors = college.honors_program_info;
    research.programs.push({
      name: honors.programName || 'Honors Program',
      details: [
        honors.benefits,
        honors.requirements,
        honors.applicationProcess
      ].filter(Boolean)
    });
  }

  return research;
};

export default useCollegeNotebook;
